// ActivitiesManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Hoot from "../../Hoot";
import { toast } from "react-toastify";

import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

import { Alert } from "@mui/material";
import {
  FiActivity,
  FiRefreshCw,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSearch,
  FiPaperclip,
  FiEye,
  FiDownload,
  FiX,
  FiCalendar,
  FiTag,
  FiDollarSign,
  FiCheckCircle,
  FiClock,
  FiPlayCircle,
  FiSlash,
  FiUpload,
} from "react-icons/fi";

// =====================================================
// Helpers
// =====================================================
const normalizeStr = (str) =>
  str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

const safeValue = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

function resolveUrl(maybeUrlOrPath) {
  if (!maybeUrlOrPath) return null;
  const s = String(maybeUrlOrPath);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${Hoot().slice(0, -1)}${s}`;
  return s;
}

function formatBytes(bytes) {
  const b = Number(bytes || 0);
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let val = b;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fileIconClass(mimeOrName = "") {
  const s = String(mimeOrName).toLowerCase();
  if (s.includes("pdf") || s.endsWith(".pdf")) return "bi bi-filetype-pdf";
  if (s.includes("image") || s.match(/\.(png|jpg|jpeg|webp|gif)$/)) return "bi bi-file-earmark-image";
  if (s.includes("word") || s.match(/\.(doc|docx)$/)) return "bi bi-filetype-doc";
  if (s.includes("excel") || s.match(/\.(xls|xlsx|csv)$/)) return "bi bi-filetype-xls";
  return "bi bi-file-earmark-text";
}

function isPreviewable(file = {}) {
  const url = resolveUrl(file.url || file.path || file.download_url);
  if (!url) return false;

  const name = (file.name || "").toLowerCase();
  const mime = (file.mime || file.mimetype || "").toLowerCase();

  if (mime.startsWith("image/")) return true;
  if (mime.includes("pdf")) return true;
  if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".webp") || name.endsWith(".gif"))
    return true;
  if (name.endsWith(".pdf")) return true;

  return false;
}

function statusClass(status) {
  const s = normalizeStr(status);
  if (s === "pendente") return "s-pendente";
  if (s === "em_andamento" || s === "em andamento" || s === "emandamento") return "s-andamento";
  if (s === "concluido" || s === "concluído") return "s-concluido";
  if (s === "cancelado") return "s-cancelado";
  return "s-outro";
}

function statusLabel(status) {
  const s = normalizeStr(status);
  if (s === "pendente") return "Pendente";
  if (s === "em_andamento" || s === "em andamento" || s === "emandamento") return "Em andamento";
  if (s === "concluido" || s === "concluído") return "Concluído";
  if (s === "cancelado") return "Cancelado";
  return safeValue(status, "Sem estado");
}

function statusIcon(status) {
  const s = normalizeStr(status);
  if (s === "pendente") return <FiClock />;
  if (s === "em_andamento" || s === "em andamento" || s === "emandamento") return <FiPlayCircle />;
  if (s === "concluido" || s === "concluído") return <FiCheckCircle />;
  if (s === "cancelado") return <FiSlash />;
  return <FiActivity />;
}

function kz(amount) {
  const n = Number(amount || 0);
  return `${n.toLocaleString("pt-PT")} Kz`;
}

// =====================================================
// Styles (mesmo look dos contactos + modais bonitos)
// =====================================================
const styles = `
:root{
  --primary:#5b5cff;
  --primary2:#7c3aed;
  --bg:#f4f6fb;
  --card:#ffffff;
  --border:#e6eaf2;
  --text:#0f172a;
  --muted:#64748b;
  --shadow: 0 14px 40px rgba(15,23,42,.08);
}

.activities-shell{
  background:var(--bg);
  padding:10px;
}

.activities-card{
  background:var(--card);
  border-radius:18px;
  border:1px solid var(--border);
  padding:12px 14px 14px;
  box-shadow: var(--shadow);
}

.activities-header{
  display:flex;
  flex-wrap:wrap;
  justify-content:space-between;
  align-items:flex-start;
  gap:10px;
  margin-bottom:10px;
}
.activities-header-title{
  font-weight:700;
  font-size:1.02rem;
  display:flex;
  align-items:center;
  gap:.55rem;
  color:var(--text);
}
.activities-chip{
  display:inline-flex;
  align-items:center;
  gap:.35rem;
  padding:.28rem .62rem;
  border-radius:999px;
  background:linear-gradient(135deg, rgba(91,92,255,.14), rgba(124,58,237,.12));
  color:#4338ca;
  font-size:.78rem;
  font-weight:600;
  border:1px solid rgba(91,92,255,.16);
}

.activities-toolbar{
  margin-top:6px;
  padding:10px 10px;
  border-radius:14px;
  background:linear-gradient(180deg, rgba(148,163,184,0.12), rgba(148,163,184,0.06));
  border:1px solid rgba(226,232,240,.7);
  margin-bottom:10px;
}

.small-muted{ font-size:.78rem; color:var(--muted); }

.kpi-row{
  display:grid; 
  gap:10px;
  margin:10px 0 12px;
}
@media (max-width: 992px){
  .kpi-row{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.kpi{
  background:var(--card);
  border:1px solid var(--border);
  border-radius:16px;
  padding:10px 12px;
  box-shadow: 0 10px 28px rgba(2,6,23,.06);
  position:relative;
  overflow:hidden;
}
.kpi::before{
  content:"";
  position:absolute;
  inset:-40px -60px auto auto;
  width:120px;
  height:120px;
  border-radius:999px;
  background: radial-gradient(circle at 30% 30%, rgba(91,92,255,.18), transparent 60%);
}
.kpi-title{
  display:flex;
  align-items:center;
  gap:8px;
  font-size:.8rem;
  color:var(--muted);
  font-weight:600;
}
.kpi-value{
  margin-top:2px;
  font-size:1.18rem;
  font-weight:650;
  color:var(--text);
}
.kpi-sub{
  margin-top:2px;
  font-size:.74rem;
  color:var(--muted);
}

.activities-table{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  font-size:.86rem;
  color:var(--text);
}
.activities-table thead th{
  text-align:left;
  padding:.62rem .65rem;
  font-size:.78rem;
  font-weight:650;
  color:var(--muted);
  border-bottom:1px solid var(--border);
  background:var(--card);
  position:sticky;
  top:0;
  z-index:1;
}
.activities-table tbody td{
  padding:.62rem .65rem;
  border-bottom:1px solid var(--border);
  vertical-align:middle;
}
.activities-table tbody tr:hover{
  background:rgba(148,163,184,0.10);
}

.pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  font-size:.74rem;
  font-weight:600;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(226,232,240,.55);
  color:#0f172a;
}

.status-badge{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:6px 10px;
  border-radius:999px;
  font-size:.74rem;
  font-weight:650;
  border:1px solid rgba(0,0,0,.06);
  letter-spacing:.2px;
}
.status-dot{
  width:8px;height:8px;border-radius:999px;
  background:currentColor;
  box-shadow: 0 0 0 4px rgba(0,0,0,.04);
}
.s-pendente{ background: rgba(245,158,11,.16); color:#f59e0b; }
.s-andamento{ background: rgba(59,130,246,.14); color:#3b82f6; }
.s-concluido{ background: rgba(34,197,94,.14); color:#22c55e; }
.s-cancelado{ background: rgba(239,68,68,.14); color:#ef4444; }
.s-outro{ background: rgba(148,163,184,.20); color:#64748b; }

.btn-icon{
  width:34px;
  height:34px;
  border-radius:12px;
  border:1px solid rgba(226,232,240,.95);
  background:#fff;
  display:inline-grid;
  place-items:center;
  cursor:pointer;
  transition: transform .08s ease, background .12s ease;
}
.btn-icon:hover{ background: rgba(91,92,255,.06); }
.btn-icon:active{ transform: scale(.98); }
.btn-icon svg{ font-size:1.05rem; color:#334155; }

.actions-row{ display:flex; justify-content:flex-end; gap:8px; flex-wrap:wrap; }

/* Modal bonito (igual contactos) */
.pretty-modal .modal-dialog{ max-width: 860px; }
@media (max-width: 768px){ .pretty-modal .modal-dialog{ max-width: 96vw; } }
.pretty-modal .modal-content{
  border-radius: 18px;
  border: 1px solid rgba(226,232,240,.95);
  box-shadow: 0 30px 90px rgba(2,6,23,.22);
  overflow: hidden;
}
.pretty-modal .modal-header{
  border-bottom: 1px solid rgba(226,232,240,.9);
  background:
    radial-gradient(1000px 260px at 20% 0%, rgba(91,92,255,.16), transparent 60%),
    radial-gradient(900px 240px at 95% 0%, rgba(124,58,237,.12), transparent 55%),
    #ffffff;
  padding: 14px 16px;
}
.pretty-modal .modal-title{
  font-weight: 650;
  color: #0f172a;
  font-size: 1rem;
}
.pretty-modal .modal-body{
  padding: 14px 16px 12px;
  background: linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%);
}
.body-scroll{
  max-height: 560px;
  overflow-y: auto;
  padding-right: 4px;
}
.pretty-modal .modal-footer{
  border-top: 1px solid rgba(226,232,240,.9);
  padding: 12px 16px;
  background: #ffffff;
}

.header-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
}
.header-left{
  display:flex;
  align-items:center;
  gap:12px;
}
.icon-wrap{
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(91,92,255,.10);
  border: 1px solid rgba(91,92,255,.18);
  display:grid;
  place-items:center;
}
.title-sub{
  margin-top:2px;
  font-size: .8rem;
  color: #64748b;
  font-weight: 500;
}

.form-grid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
@media (max-width: 720px){ .form-grid{ grid-template-columns: 1fr; } }

.cardlet{
  background: rgba(255,255,255,.9);
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  padding: 10px 12px;
}
.cardlet-title{
  font-size: .78rem;
  color:#64748b;
  font-weight: 600;
  display:flex;
  align-items:center;
  gap:8px;
  margin-bottom: 8px;
}
.req{ color:#ef4444; font-weight:700; }

.form-label{
  font-size: .78rem !important;
  color: #475569 !important;
  font-weight: 600 !important;
  margin-bottom: 6px !important;
}
.form-control, .form-select{
  border-radius: 3px !important;
  border-color: rgba(226,232,240,.95) !important;
  padding-top: .55rem !important;
  padding-bottom: .55rem !important;
}
.form-control:focus, .form-select:focus{
  box-shadow: 0 0 0 .2rem rgba(91,92,255,.15) !important;
  border-color: rgba(91,92,255,.45) !important;
}
.helper{
  font-size: .74rem;
  color:#64748b;
  margin-top: 6px;
}

.file-list{
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px;
  overflow:hidden;
  background:#fff;
}
.file-item{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:10px 12px;
  border-bottom:1px solid rgba(226,232,240,.9);
}
.file-item:last-child{ border-bottom:none; }
.file-left{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}
.file-name{
  font-weight:650;
  color:var(--text);
  font-size:.86rem;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  max-width: 360px;
}
.file-meta{ font-size:.74rem; color:var(--muted); margin-top:2px; }
.file-actions{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
`;

// =====================================================
// Component
// =====================================================
export default function ActivitiesManager() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal form (registar/editar)
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  // Modal anexos (upload)
  const [openDocs, setOpenDocs] = useState(false);
  const [docsActivity, setDocsActivity] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [docNote, setDocNote] = useState("");
  const [docFiles, setDocFiles] = useState([]);

  // Modal detalhes anexos (lista + preview/download)
  const [docsDetailsOpen, setDocsDetailsOpen] = useState(false);
  const [docsDetailsRow, setDocsDetailsRow] = useState(null);

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const [form, setForm] = useState({
    title: "",
    category: "interno",
    status: "pendente",
    start_date: "",
    end_date: "",
    cost: 0,
    description: "",
  });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(Hoot() + "activities/list", { withCredentials: true });
      setRows(res.data || []);
    } catch {
      setError("Erro ao carregar actividades.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = normalizeStr(q);
    return (rows || []).filter((r) => {
      if (statusFilter !== "all" && normalizeStr(r.status) !== normalizeStr(statusFilter)) return false;

      const hay = [
        r.title,
        r.category,
        r.status,
        r.description,
        r.start_date,
        r.end_date,
        String(r.cost ?? ""),
      ]
        .map(normalizeStr)
        .join(" ");

      return hay.includes(s);
    });
  }, [rows, q, statusFilter]);

  // KPIs
  const kpis = useMemo(() => {
    const out = {
      total: filtered.length,
      pendente: 0,
      em_andamento: 0,
      concluido: 0,
      cancelado: 0,
      totalCost: 0,
    };
    filtered.forEach((r) => {
      const s = normalizeStr(r.status);
      if (s === "pendente") out.pendente++;
      else if (s === "em_andamento" || s === "em andamento" || s === "emandamento") out.em_andamento++;
      else if (s === "concluido" || s === "concluído") out.concluido++;
      else if (s === "cancelado") out.cancelado++;
      out.totalCost += Number(r.cost || 0);
    });
    return out;
  }, [filtered]);

  const openCreate = () => {
    setEditing(null);
    setError(null);
    setForm({
      title: "",
      category: "interno",
      status: "pendente",
      start_date: "",
      end_date: "",
      cost: 0,
      description: "",
    });
    setOpenForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setError(null);
    setForm({
      title: row.title || "",
      category: row.category || "interno",
      status: row.status || "pendente",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      cost: row.cost ?? 0,
      description: row.description || "",
    });
    setOpenForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setOpenForm(false);
    setEditing(null);
  };

  const saveActivity = async () => {
    if (!form.title.trim()) {
      setError("Título é obrigatório.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...form,
        cost: Number(form.cost || 0),
      };

      if (editing?.id) {
        await axios.put(Hoot() + `activities/${editing.id}`, payload, { withCredentials: true });
        toast.success("Actividade atualizada com sucesso!");
      } else {
        await axios.post(Hoot() + "activities/create", payload, { withCredentials: true });
        toast.success("Actividade registada com sucesso!");
      }

      closeForm();
      await load();
    } catch {
      setError("Erro ao guardar actividade.");
    } finally {
      setSaving(false);
    }
  };

  const deleteActivity = async (row) => {
    if (!window.confirm(`Remover actividade "${row.title}"?`)) return;
    try {
      setSaving(true);
      setError(null);
      await axios.delete(Hoot() + `activities/${row.id}`, { withCredentials: true });
      toast.success("Actividade removida.");
      await load();
    } catch {
      setError("Erro ao remover actividade.");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------
  // Anexos (Upload)
  // -------------------------
  const openUploadDocs = (row) => {
    setDocsActivity(row);
    setDocTitle("");
    setDocNote("");
    setDocFiles([]);
    setError(null);
    setOpenDocs(true);
  };

  const closeUploadDocs = () => {
    if (saving) return;
    setOpenDocs(false);
    setDocsActivity(null);
    setDocFiles([]);
  };

  const uploadDocs = async () => {
    if (!docsActivity?.id) return;
    if (!docTitle.trim()) {
      setError("Título do documento é obrigatório.");
      return;
    }
    if (!docFiles.length) {
      setError("Selecione pelo menos 1 ficheiro.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const fd = new FormData();
      fd.append("title", docTitle);
      fd.append("note", docNote);
      for (const f of docFiles) fd.append("files", f);

      await axios.post(Hoot() + `activities/${docsActivity.id}/docs/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Anexos enviados com sucesso!");
      closeUploadDocs();
      await load();
    } catch {
      setError("Erro ao enviar anexos da actividade.");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------
  // Anexos (Detalhes + list)
  // -------------------------
  const handleOpenDocsDetails = async (row) => {
    try {
      setError(null);
      let full = row;

      // se list não traz docs, tenta buscar:
      if (!row?.docs && !row?.files) {
        try {
          const res = await axios.get(Hoot() + `activities/${row.id}/docs/list`, { withCredentials: true });
          // esperamos { data: [...] } ou [...]
          const list = res.data?.data || res.data || [];
          full = { ...row, docs: list };
        } catch {
          // segue com o que tiver
        }
      }

      setDocsDetailsRow(full);
      setDocsDetailsOpen(true);
    } catch {
      setError("Erro ao abrir anexos.");
    }
  };

  const closeDocsDetails = () => {
    setDocsDetailsOpen(false);
    setDocsDetailsRow(null);
  };

  const downloadFile = (file) => {
    const url = resolveUrl(file?.url || file?.path || file?.download_url);
    if (!url) return toast.error("URL do ficheiro não disponível.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const preview = (file) => {
    const url = resolveUrl(file?.url || file?.path || file?.download_url);
    if (!url) return toast.error("URL do ficheiro não disponível.");

    setPreviewFile({
      url,
      name: file?.name || "Ficheiro",
      mime: file?.mime || file?.mimetype || "",
    });
    setShowPreview(true);
  };

  // =====================================================
  // UI pieces
  // =====================================================
  const StatusBadge = ({ value }) => (
    <span className={`status-badge ${statusClass(value)}`}>
      <span className="status-dot" />
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {statusIcon(value)}
        {statusLabel(value)}
      </span>
    </span>
  );

  return (
    <>
      <style>{styles}</style>

      <div className="activities-shell">
        <div className="activities-card">
          {/* HEADER */}
          <div className="activities-header">
            <div className="activities-header-title">
              <FiActivity style={{ color: "var(--primary)", fontSize: 20 }} />
              <span>Actividades internas</span>
              <span className="activities-chip">{filtered.length} actividades</span>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center"
                onClick={load}
                disabled={loading}
              >
                <FiRefreshCw className="me-2" />
                {loading ? "A carregar..." : "Recarregar"}
              </button>

              <button
                className="btn btn-outline-primary btn-sm rounded-pill d-flex align-items-center"
                onClick={openCreate}
                disabled={saving}
              >
                <FiPlus className="me-2" />
                Adicionar
              </button>
            </div>
          </div>

          {/* ERROR */}
          {error ? (
            <Alert severity="error" className="mb-2">
              {error}
            </Alert>
          ) : null}

          {/* TOOLBAR */}
          <div className="activities-toolbar">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-lg-8">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <FiSearch />
                  </span>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Pesquisar (título, categoria, descrição, datas...)"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos estados</option>
                  <option value="pendente">Pendente</option>
                  <option value="em_andamento">Em andamento</option>
                  <option value="concluido">Concluído</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>
          </div>

          {/* KPI */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-title">
                <FiActivity /> Total (filtro)
              </div>
              <div className="kpi-value">{kpis.total}</div>
              <div className="kpi-sub">Todas as actividades encontradas</div>
            </div>

            <div className="kpi">
              <div className="kpi-title" style={{ color: "#f59e0b" }}>
                <FiClock /> Pendentes
              </div>
              <div className="kpi-value">{kpis.pendente}</div>
              <div className="kpi-sub">Aguardam execução</div>
            </div>

            <div className="kpi">
              <div className="kpi-title" style={{ color: "#3b82f6" }}>
                <FiPlayCircle /> Em andamento
              </div>
              <div className="kpi-value">{kpis.em_andamento}</div>
              <div className="kpi-sub">Em progresso</div>
            </div>

            <div className="kpi">
              <div className="kpi-title" style={{ color: "#22c55e" }}>
                <FiCheckCircle /> Concluídas
              </div>
              <div className="kpi-value">{kpis.concluido}</div>
              <div className="kpi-sub">
                Custo total: <b>{kz(kpis.totalCost)}</b>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div style={{ overflowX: "auto" }}>
            <table className="activities-table">
              <thead>
                <tr>
                  <th>Datas</th>
                  <th>Título</th>
                  <th>Categoria</th>
                  <th>Estado</th>
                  <th>Custo</th>
                  <th>Anexos</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="d-flex flex-column" style={{ lineHeight: 1.25 }}>
                        <span className="small-muted d-flex align-items-center gap-2">
                          <FiCalendar />
                          Início: <b style={{ color: "var(--text)" }}>{safeValue(r.start_date)}</b>
                        </span>
                        <span className="small-muted d-flex align-items-center gap-2">
                          <FiCalendar />
                          Fim: <b style={{ color: "var(--text)" }}>{safeValue(r.end_date)}</b>
                        </span>
                      </div>
                    </td>

                    <td style={{ fontWeight: 650 }}>
                      {safeValue(r.title, "Sem título")}
                      {r.description ? (
                        <div className="small-muted" style={{ marginTop: 4 }}>
                          {r.description}
                        </div>
                      ) : null}
                    </td>

                    <td>
                      <span className="pill">
                        <FiTag />
                        {safeValue(r.category, "—")}
                      </span>
                    </td>

                    <td>
                      <StatusBadge value={r.status} />
                    </td>

                    <td>
                      <span className="pill">
                        <FiDollarSign />
                        {kz(r.cost)}
                      </span>
                    </td>

                    <td>
                      <span className="pill">
                        <FiPaperclip />
                        {r.docs_count ?? r.docs?.length ?? 0}
                      </span>
                    </td>

                    <td className="text-end">
                      <div className="actions-row">
                        <button
                          className="btn-icon"
                          title="Ver anexos"
                          onClick={() => handleOpenDocsDetails(r)}
                          disabled={saving}
                        >
                          <FiEye />
                        </button>

                        <button
                          className="btn-icon"
                          title="Adicionar anexos"
                          onClick={() => openUploadDocs(r)}
                          disabled={saving}
                        >
                          <FiUpload />
                        </button>

                        <button className="btn-icon" title="Editar" onClick={() => openEdit(r)} disabled={saving}>
                          <FiEdit3 />
                        </button>

                        <button className="btn-icon" title="Remover" onClick={() => deleteActivity(r)} disabled={saving}>
                          <FiTrash2 style={{ color: "#ef4444" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <span className="small-muted">Sem dados.</span>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* =====================================================
          MODAL FORM (registo/edição) - estilo contactos
         ===================================================== */}
      <Modal show={openForm} onHide={closeForm} centered backdrop="static" keyboard={!saving} className="pretty-modal">
        <Modal.Header closeButton={!saving}>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiActivity style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>{editing ? "Atualizar actividade" : "Registar actividade"}</div>
                <div className="title-sub">Define datas, custo, estado e descrição.</div>
              </div>
            </div>

            <span className={`status-badge ${statusClass(form.status)}`}>
              <span className="status-dot" />
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {statusIcon(form.status)}
                {statusLabel(form.status)}
              </span>
            </span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {error ? (
              <div className="alert alert-danger py-2 mb-3" style={{ borderRadius: 14 }}>
                {error}
              </div>
            ) : null}

            <Form>
              <div className="form-grid">
                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <i className="bi bi-card-text" />
                    Identificação
                  </div>

                  <div className="form-grid">
                    <Form.Group>
                      <Form.Label>
                        Título <span className="req">*</span>
                      </Form.Label>
                      <Form.Control
                        value={form.title}
                        onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="Ex.: Formação equipa"
                        required
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Categoria</Form.Label>
                      <Form.Select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                        <option value="interno">Interno</option>
                        <option value="viagem">Viagem</option>
                        <option value="formacao">Formação</option>
                        <option value="suporte">Suporte</option>
                        <option value="outro">Outro</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>

                <div className="cardlet">
                  <div className="cardlet-title">
                    <FiCalendar />
                    Datas
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Data início</Form.Label>
                    <Form.Control type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Data fim</Form.Label>
                    <Form.Control type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} />
                  </Form.Group>
                </div>

                <div className="cardlet">
                  <div className="cardlet-title">
                    <FiDollarSign />
                    Estado & custo
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                      <option value="pendente">Pendente</option>
                      <option value="em_andamento">Em andamento</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Custo (Kz)</Form.Label>
                    <Form.Control type="number" value={form.cost} onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))} placeholder="0" />
                    <div className="helper">Use apenas números. Ex.: 15000</div>
                  </Form.Group>
                </div>

                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <i className="bi bi-sticky" />
                    Descrição
                  </div>

                  <Form.Group>
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Opcional..." />
                  </Form.Group>
                </div>
              </div>
            </Form>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 flex-wrap w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>

            <Button variant="primary" onClick={saveActivity} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  A guardar...
                </>
              ) : editing ? (
                "Atualizar"
              ) : (
                "Registar"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          MODAL UPLOAD ANEXOS - estilo contactos
         ===================================================== */}
      <Modal show={openDocs} onHide={closeUploadDocs} centered backdrop="static" keyboard={!saving} className="pretty-modal">
        <Modal.Header closeButton={!saving}>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiPaperclip style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>Anexos da actividade</div>
                <div className="title-sub">{safeValue(docsActivity?.title)}</div>
              </div>
            </div>

            <span className="pill">
              <FiPaperclip />
              {docFiles.length} selecionado(s)
            </span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {error ? (
              <div className="alert alert-danger py-2 mb-3" style={{ borderRadius: 14 }}>
                {error}
              </div>
            ) : null}

            <Form>
              <div className="form-grid">
                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <FiPaperclip />
                    Documento
                  </div>

                  <div className="form-grid">
                    <Form.Group>
                      <Form.Label>
                        Título <span className="req">*</span>
                      </Form.Label>
                      <Form.Control value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Ex.: Fatura / Relatório" />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Nota</Form.Label>
                      <Form.Control value={docNote} onChange={(e) => setDocNote(e.target.value)} placeholder="Opcional..." />
                    </Form.Group>

                    <Form.Group style={{ gridColumn: "1 / -1" }}>
                      <Form.Label>
                        Ficheiros <span className="req">*</span>
                      </Form.Label>
                      <Form.Control type="file" multiple onChange={(e) => setDocFiles(Array.from(e.target.files || []))} />
                      <div className="helper">Selecione 1 ou mais ficheiros.</div>

                      {docFiles.length ? (
                        <div className="mt-2 file-list">
                          {docFiles.map((f, idx) => (
                            <div className="file-item" key={`${f.name}-${idx}`}>
                              <div className="file-left">
                                <div className="btn-icon" style={{ cursor: "default", width: 34, height: 34 }}>
                                  <i className={fileIconClass(f.type || f.name)} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div className="file-name">{f.name}</div>
                                  <div className="file-meta">
                                    {formatBytes(f.size)} {f.type ? `• ${f.type}` : ""}
                                  </div>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm rounded-pill"
                                onClick={() => setDocFiles((prev) => prev.filter((_, i) => i !== idx))}
                                disabled={saving}
                              >
                                <FiX className="me-1" />
                                Remover
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </Form.Group>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 flex-wrap w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeUploadDocs} disabled={saving}>
              Cancelar
            </Button>

            <Button variant="primary" onClick={uploadDocs} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  A enviar...
                </>
              ) : (
                "Enviar"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          MODAL: Ver anexos (lista + preview/download) - estilo contactos
         ===================================================== */}
      <Modal show={docsDetailsOpen} onHide={closeDocsDetails} centered backdrop="static" keyboard className="pretty-modal">
        <Modal.Header closeButton>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiEye style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>Anexos</div>
                <div className="title-sub">{safeValue(docsDetailsRow?.title)}</div>
              </div>
            </div>

            <span className="pill">
              <FiPaperclip />
              {docsDetailsRow?.docs?.length ?? docsDetailsRow?.files?.length ?? docsDetailsRow?.docs_count ?? 0} ficheiro(s)
            </span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            <div className="cardlet mb-3">
              <div className="cardlet-title">
                <i className="bi bi-info-circle" />
                Actividade
              </div>

              <div className="form-grid">
                <div>
                  <div className="small-muted">Título</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(docsDetailsRow?.title)}</div>
                </div>
                <div>
                  <div className="small-muted">Estado</div>
                  <div style={{ marginTop: 2 }}>
                    <StatusBadge value={docsDetailsRow?.status} />
                  </div>
                </div>
                <div>
                  <div className="small-muted">Início</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(docsDetailsRow?.start_date)}</div>
                </div>
                <div>
                  <div className="small-muted">Fim</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(docsDetailsRow?.end_date)}</div>
                </div>
              </div>
            </div>

            <div className="cardlet">
              <div className="cardlet-title">
                <FiPaperclip />
                Ficheiros
              </div>

              <div className="file-list">
                {((docsDetailsRow?.docs || docsDetailsRow?.files) || []).map((f, idx) => {
                  const url = resolveUrl(f.url || f.path || f.download_url);
                  const canPreview = isPreviewable(f);

                  return (
                    <div className="file-item" key={f.id || `${f.name}-${idx}`}>
                      <div className="file-left">
                        <div className="btn-icon" style={{ cursor: "default", width: 34, height: 34 }}>
                          <i className={fileIconClass(f.mime || f.name)} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="file-name">{safeValue(f.name, "Ficheiro")}</div>
                          <div className="file-meta">
                            {formatBytes(f.size)} {f.mime ? `• ${f.mime}` : ""} {url ? "" : " • (sem link)"}
                          </div>
                        </div>
                      </div>

                      <div className="file-actions">
                        {canPreview ? (
                          <button className="btn btn-outline-primary btn-sm rounded-pill" type="button" onClick={() => preview(f)} disabled={!url}>
                            <FiEye className="me-1" />
                            Preview
                          </button>
                        ) : null}

                        <button className="btn btn-outline-dark btn-sm rounded-pill" type="button" onClick={() => downloadFile(f)} disabled={!url}>
                          <FiDownload className="me-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  );
                })}

                {(((docsDetailsRow?.docs || docsDetailsRow?.files) || []).length === 0) ? (
                  <div className="file-item">
                    <div className="small-muted">Sem ficheiros (ou o endpoint não devolveu a lista).</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeDocsDetails}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          MODAL: Preview (PDF/Imagem)
         ===================================================== */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered backdrop="static" keyboard className="pretty-modal">
        <Modal.Header closeButton>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiEye style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>Preview</div>
                <div className="title-sub">{safeValue(previewFile?.name)}</div>
              </div>
            </div>

            <button
              className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center"
              onClick={() => previewFile?.url && window.open(previewFile.url, "_blank", "noopener,noreferrer")}
            >
              <FiDownload className="me-2" />
              Abrir
            </button>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {!previewFile?.url ? (
              <div className="alert alert-warning" style={{ borderRadius: 14 }}>
                Sem URL para preview.
              </div>
            ) : previewFile?.mime?.toLowerCase().includes("pdf") || (previewFile?.name || "").toLowerCase().endsWith(".pdf") ? (
              <div style={{ border: "1px solid rgba(226,232,240,.95)", borderRadius: 14, overflow: "hidden" }}>
                <iframe title="PDF Preview" src={previewFile.url} style={{ width: "100%", height: "70vh", border: "none" }} />
              </div>
            ) : (
              <div style={{ border: "1px solid rgba(226,232,240,.95)", borderRadius: 14, overflow: "hidden" }}>
                <img
                  src={previewFile.url}
                  alt="Preview"
                  style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", background: "#fff" }}
                />
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
